import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { faDatabase } from '@fortawesome/pro-regular-svg-icons';
import { faPlus, faTrashXmark } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { Disk } from '@typings';
import { getDisks } from '@services';
import { clusterContextSelector, contextNamesSelector } from '@selectors';
import { useFetchList, useHelmetTitle, useSelector } from '@hooks';
import { as, getFormattedMemory } from '@utils';

import {
  Button,
  ChaseSpinner,
  Field,
  Helmet,
  Icon,
  Modal,
  Table,
} from '@components';
import {
  CreateDiskModal,
  DeleteDiskModal,
  DiskDetails,
} from '@components/Disks';
import { Layout } from '@components/Layouts';
import { EmptyContent } from '@components/Ui';

export default function DisksPage() {
  const cluster = useSelector(clusterContextSelector);
  const { organizationName, projectName } = useSelector(contextNamesSelector);

  const { makeTitle } = useHelmetTitle();
  const {
    isFetched,
    isEmpty,
    list: disks,
    getList,
  } = useFetchList<Disk[]>({
    getList: getDisks,
    fetchOnMount: false,
  });

  const [search, setSearch] = useState('');
  const [diskId, setDiskId] = useState<string | null>(null);

  const { disksUrl } = cluster! ?? {};

  /**
   * Used for disk details drawer
   */
  const disk = disks.find(({ id }) => id === diskId);

  const fetchDisks = useCallback(async () => {
    await getList({ disksUrl, organizationName, projectName });
  }, [organizationName, projectName, disksUrl, getList]);

  useEffect(() => {
    fetchDisks();
  }, [fetchDisks]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(event.target.value);

  const handleDiskIdReset = () => {
    setDiskId(null);
  };

  const makeListItem = ({
    id,
    name,
    status,
    storage,
    usedBytes,
    owner,
    createdAt,
  }: Disk) => {
    return (
      <Table.Row
        key={id}
        onClick={() => setDiskId(id)}
        className={clsx('cursor-pointer', {
          '[&>td]:bg-background': diskId === id,
        })}
      >
        <Table.Cell className="pr-1">
          <Icon icon={faDatabase} size="lg" className="text-success" />
        </Table.Cell>
        <Table.Cell className="truncate">
          <p title={name ?? id} className="truncate">
            {name ?? id}
          </p>
        </Table.Cell>
        <Table.Cell>
          {getFormattedMemory(usedBytes ?? 0)} / {getFormattedMemory(storage)}
        </Table.Cell>
        <Table.Cell>{owner}</Table.Cell>
        <Table.Cell>{status}</Table.Cell>
        <Table.Cell>{dayjs(createdAt).format('ll')}</Table.Cell>
        <Table.Cell className="flex-row">
          <Modal
            content={
              <DeleteDiskModal
                id={id}
                name={name ?? id}
                getDisks={fetchDisks}
              />
            }
          >
            <Button theme>
              <Icon
                icon={faTrashXmark}
                className="text-error-light transition-colors hover:text-error"
              />
            </Button>
          </Modal>
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderContent = () => {
    const list = search
      ? disks.filter(
          ({ id, name }) =>
            as(name, '').includes(search) || id.includes(search),
        )
      : disks;

    if (!isFetched) {
      return (
        <div className="relative flex min-h-[400px] w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-12 w-12" />
        </div>
      );
    }

    if (isEmpty) {
      return (
        <EmptyContent
          icon={faDatabase}
          className="mt-20"
          title="No disks found"
        />
      );
    }

    return (
      <Table className="w-full auto-rows-min grid-cols-[min-content_minmax(320px,auto)_repeat(2,minmax(160px,max-content))_max-content_max-content_min-content] overflow-auto">
        <Table.Header>
          <Table.Row>
            {/* Icon */}
            <Table.Head />
            <Table.Head>Name</Table.Head>
            <Table.Head>Used / Storage</Table.Head>
            <Table.Head>Owner</Table.Head>
            <Table.Head>Status</Table.Head>
            <Table.Head>Created</Table.Head>
            {/* Actions */}
            <Table.Head />
          </Table.Row>
        </Table.Header>
        <Table.Body>{list.map(makeListItem)}</Table.Body>
      </Table>
    );
  };

  return (
    <Layout title="Disks">
      <Helmet
        title={makeTitle('Disks', '%p', '%c')}
        description="Easily manage your disks. Access detailed information and control settings for your disks, ensuring efficient organization and management of your storage resources"
      />
      <Layout.Content className="flex items-start gap-10">
        <div className="flex flex-1 flex-col">
          <div className="mb-14 flex items-center gap-10">
            <Modal content={<CreateDiskModal getDisks={fetchDisks} />}>
              <Button variant="rebecca" className="gap-1 capitalize">
                <Icon icon={faPlus} />
                Create new disk
              </Button>
            </Modal>
            <Field.Input
              containerClassName="flex-1"
              className="border-neural-03"
              label="Search"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          {renderContent()}
        </div>
        <DiskDetails
          disk={disk}
          resetDiskId={handleDiskIdReset}
          getDisks={fetchDisks}
        />
      </Layout.Content>
    </Layout>
  );
}
